import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import { useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragMoveEvent
} from "@dnd-kit/core";
import { restrictToVerticalAxis, snapCenterToCursor } from "@dnd-kit/modifiers";
import { Flex, usePortalContainer } from "@theorgchartdev/storybook";
import { ConditionInput } from "./ConditionInput";
import { BlockCriteria } from "./BlockCriteria";
import { enumRulePairLogicCondition, QRulePair } from "@coreapp/CoreApp/QRulePair";
import React from "react";
import { type RuleStatus } from "./useChartRuleEditorController";
import { UIRuleError } from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";

type Props = {
  rules: QRulePair[];
  ruleEditStatus: RuleStatus;
  toggleRuleEditStatus: (rule: QRulePair) => void;
  handleRuleChange: (index: number, rule: QRulePair) => void;
  handleRuleRemove: (index: number) => void;
  handleInsertRuleAt: (
    ruleIndex: number,
    rule: QRulePair,
    begginingOfOrBlock: boolean,
    endOfOrBlock: boolean
  ) => void;
  handleInsertEmptyRuleAt: (
    connectsWithCondition: string,
    ruleCondition: string,
    index?: number
  ) => void;
  testIdPrefix?: string;
  planningTestIdPrefix?: string;
  ruleErrorsByUID?: Record<number, UIRuleError>;
  onDragStarted?: () => void;
  onDragEnded?: () => void;
};

enum RelPos {
  above,
  betweenNotPassed,
  betweenPassed,
  below
}

enum ListBound {
  beggining,
  end
}

type DragRuleData = {
  rule: QRulePair;
  ruleIndex: number;
  criteriaN: string;
};

type DraggedElemData = {
  index: Nullable<number>;
  relativePosition: Nullable<RelPos>;
  overIndex: Nullable<number>;
};

export const BlockCriteriaContainer = (props: Props) => {
  const {
    rules,
    ruleEditStatus,
    toggleRuleEditStatus,
    handleRuleChange,
    handleRuleRemove,
    handleInsertRuleAt,
    testIdPrefix,
    planningTestIdPrefix,
    ruleErrorsByUID,
    onDragStarted,
    onDragEnded
  } = props;

  const portalContainer = usePortalContainer();

  const orBlocks = QRulePair.SplitIntoOrBlocks(rules, false);

  const [activeDragData, setDragData] = useState<Nullable<DragRuleData>>(null);
  const [overData, setOverData] = useState<DraggedElemData>({
    index: null,
    relativePosition: null,
    overIndex: null
  });

  const sortableItems = rules.map((_, ruleIndex) => ({
    id: `rule-${ruleIndex}`
  }));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (rules.length <= 1) return;

    const data = event.active?.data?.current;

    const rule = data?.rule;
    const ruleIndex = data?.ruleIndex;
    const criteriaN = data?.criteriaN;

    setDragData({ rule, ruleIndex, criteriaN });
    onDragStarted?.();
    handleRuleRemove(ruleIndex);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!activeDragData) {
      return;
    }

    const { rule } = activeDragData;
    const data = event.over?.data.current;

    const ruleIndex = data?.ruleIndex;
    const initialTopFramePos = event.active.rect?.current?.translated?.top;
    const overTopFramePos = event.over?.rect?.top;

    const willInsertInOrBlockAbove = willInsertInOrBlock(ListBound.beggining, ruleIndex);
    const willInsertInOrBlockBelow = willInsertInOrBlock(ListBound.end, ruleIndex);

    if (initialTopFramePos && overTopFramePos && overTopFramePos < initialTopFramePos) {
      handleInsertRuleAt(ruleIndex + 1, rule, willInsertInOrBlockAbove, willInsertInOrBlockBelow);
    } else {
      handleInsertRuleAt(ruleIndex, rule, willInsertInOrBlockAbove, willInsertInOrBlockBelow);
    }

    onDragEnded?.();
    setDragData(null);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const draggedTopFramePos = event.active.rect?.current?.translated?.top || 0;
    const draggedBottomFramePos = event.active.rect?.current?.translated?.bottom || 0;
    const overTopFramePos = event.over?.rect?.top || 0;
    const overBottomFramePos = event.over?.rect?.bottom || 0;

    const totallyUp = overTopFramePos > draggedBottomFramePos;
    const totallyDown = overBottomFramePos < draggedTopFramePos;
    const betweenNotPassed =
      draggedBottomFramePos > overTopFramePos && draggedBottomFramePos < overBottomFramePos;
    const betweenPassed =
      draggedTopFramePos > overTopFramePos && draggedTopFramePos < overBottomFramePos;

    let index = event.over?.data.current?.ruleIndex;

    if (betweenPassed || totallyDown) {
      index++;
    }

    setOverData(() => ({
      index: index,
      overIndex: event.over?.data.current?.ruleIndex,
      relativePosition: totallyUp
        ? RelPos.above
        : betweenNotPassed
          ? RelPos.betweenNotPassed
          : betweenPassed
            ? RelPos.betweenPassed
            : RelPos.below
    }));
  };

  const changeCondition = (condition: string, index: number) => {
    const newRule = rules[index].Clone();
    newRule.fCondition = condition;
    handleRuleChange(index, newRule);
  };

  const draggableSeparator = <div className="oc-inter-filter-drag-placeholder" />;

  const willInsertInOrBlock = (pos: ListBound, index: number) => {
    if (pos === ListBound.beggining) {
      return (
        overData.index === index &&
        overData.overIndex === index &&
        !(index === 0 && overData.relativePosition === RelPos.above)
      );
    } else {
      return (
        overData.index === index + 1 &&
        overData.overIndex === index &&
        !(index === rules.length - 1 && overData.relativePosition === RelPos.below)
      );
    }
  };

  let totalIndex = 0;
  const renderRules = orBlocks.map((orBlock, index) => {
    const orBlockLength = orBlock.length;
    const orBlockKey = orBlock.map(rule => rule.fUID).join();
    const currentRules = [];
    let ruleIndex = 0;

    for (let rule of orBlock) {
      const criteriaN = QRulePair.CriteriaLabel(index, orBlockLength, ruleIndex);
      const currentIndex = totalIndex;

      // Drag rules consts
      const lastOfBlock = ruleIndex === orBlock.length - 1;

      currentRules.push(
        <React.Fragment key={rule.fUID}>
          {activeDragData &&
            willInsertInOrBlock(ListBound.beggining, totalIndex) &&
            draggableSeparator}
          <BlockCriteria
            isEditing={ruleEditStatus[rule.fUID] ?? false}
            toggleEditStatus={() => toggleRuleEditStatus(rule)}
            ruleIndex={currentIndex}
            rule={rule}
            criteriaN={criteriaN}
            setRule={rule => handleRuleChange(currentIndex, rule)}
            handleRuleRemove={() => handleRuleRemove(currentIndex)}
            insertEmptyRuleNextToThisRule={() =>
              props.handleInsertEmptyRuleAt(
                enumRulePairLogicCondition.or,
                lastOfBlock ? enumRulePairLogicCondition.and : enumRulePairLogicCondition.or,
                currentIndex + 1
              )
            }
            testIdPrefix={testIdPrefix}
            planningTestIdPrefix={planningTestIdPrefix}
            errorsObject={ruleErrorsByUID?.[rule.fUID]}
          />
          {!lastOfBlock && (
            <Flex direction={"row"} margin={"0 0 8px"}>
              <ConditionInput
                isEditing={true}
                value={rule.fCondition}
                onChange={value => changeCondition(value.value, currentIndex)}
                dataTestId={testIdPrefix ? `${testIdPrefix}-condition-toggle` : undefined}
              />
            </Flex>
          )}
          {activeDragData && willInsertInOrBlock(ListBound.end, totalIndex) && draggableSeparator}
        </React.Fragment>
      );
      totalIndex++;
      ruleIndex++;
    }

    const lastRule = orBlock[orBlock.length - 1];
    const lastRuleIndex = totalIndex - 1;
    return (
      <React.Fragment key={orBlockKey}>
        <div
          className={
            orBlockLength > 1 ? "ocn-filter-or-block-multiple" : "ocn-filter-or-block-single"
          }
        >
          {currentRules}
        </div>
        {index < orBlocks.length - 1 && (
          <Flex direction={"row"} margin={"0 0 8px"}>
            <ConditionInput
              isEditing={true}
              value={lastRule.fCondition}
              onChange={value => changeCondition(value.value, lastRuleIndex)}
              dataTestId={testIdPrefix ? `${testIdPrefix}-condition-toggle` : undefined}
            />
          </Flex>
        )}
      </React.Fragment>
    );
  });

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
      sensors={sensors}
      collisionDetection={closestCenter}
    >
      <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
        <Flex direction={"column"}>
          {activeDragData &&
            overData.index === 0 &&
            overData.relativePosition === RelPos.above &&
            draggableSeparator}
          {renderRules}
          {activeDragData &&
            overData.index === rules.length &&
            overData.relativePosition === RelPos.below &&
            draggableSeparator}
        </Flex>
      </SortableContext>
      {activeDragData &&
        createPortal(
          <DragOverlay
            className={"oc-drag-overlay"}
            modifiers={[restrictToVerticalAxis, snapCenterToCursor]}
            style={{ height: "45px" }}
          >
            <BlockCriteria
              isEditing={ruleEditStatus[activeDragData.rule.fUID]}
              ruleIndex={activeDragData.ruleIndex}
              rule={activeDragData.rule}
              criteriaN={activeDragData.criteriaN}
              setRule={() => {}}
              handleRuleRemove={() => {}}
              insertEmptyRuleNextToThisRule={() => {}}
            />
          </DragOverlay>,
          portalContainer ?? document.body
        )}
    </DndContext>
  );
};
